<!-- Main Fahrdienst 452 Module view with document viewer and "Perlschnur" display -->
<template>
  <responsive-drawer-layout v-if="isLoaded" :isContentPdf="displayedDocumentUrl !== null" contentUrl="pdfJsDocumentUrl">
    <template v-slot:drawer>
      <full-height-container>
        <div style="top: 0" :style="`height:${dutyFilesHeight}px`" class="fill-width-absolute">
          <layout-container>
            <template v-slot:header-row-1>
              <div class="subtitle-2 pl-3">
                {{ $t(fahrdienstDutyTypeNames) }}
                <!-- <sort-menu class="mb-2 d-flex" :color="theme.appBar.other.color" v-model="sorterName"></sort-menu> -->
              </div>
            </template>

            <!-- These are the duty files (usually only one) -->
            <template v-slot:content>
              <fahrdienst-doc-list
                :documents="dutyFiles"
                :selectedDocument="displayedDocument"
                :sorterName="sorterName"
                ignoreReadState
                @onSelected="onDocumentSelected"
              >
              </fahrdienst-doc-list>
            </template>
          </layout-container>
        </div>

        <div
          style="bottom: 0; overflow-y: auto"
          :style="`top:${dutyFilesHeight}px;`"
          :class="`item-top-border`"
          class="fill-width-absolute zirkular-scroll-container"
        >
          <div v-for="zirkular in zirkularTypeWithFiles" :key="zirkular.id" class="zirkular-section">
            <div class="zirkular-header" :class="{ 'zirkular-header-dark': darkMode, 'zirkular-header-light': !darkMode }">
              <div class="subtitle-2 pl-3 pt-3">{{ $t(zirkular.name) }}</div>
              <div class="d-flex align-center justify-space-between px-3 pb-2">
                <v-btn v-if="groupedFiles[zirkular.id]" small text @click="toggleRelated(zirkular.id)">
                  {{
                    relatedDocsOnly[zirkular.id]
                      ? $t('fahrdienst.showAllDocs', { type: zirkular.name })
                      : $t('fahrdienst.showRelatedDocs', { type: zirkular.name, duty: dutyTitle })
                  }}
                </v-btn>
                <sort-menu v-model="sorterName"></sort-menu>
              </div>
            </div>

            <div class="zirkular-content">
              <!-- Zirkulare -->
              <fahrdienst-doc-list
                :documents="groupedFiles[String(zirkular.id)]"
                :selectedDocument="displayedDocument"
                :sorterName="sorterName"
                @onSelected="onDocumentSelected"
              ></fahrdienst-doc-list>
            </div>
          </div>
        </div>
      </full-height-container>
    </template>

    <!-- Page content -->
    <template v-slot:content>
      <!-- Document display -->
      <transition v-if="isLoaded && displayedDocumentUrl" name="fade">
        <div class="mx-0 fahrdienstPdfContainer inherit-height">
          <pdf-js
            v-if="displayedDocumentUrl"
            :url="displayedDocumentUrl"
            :download-file-name="downloadFileName"
            :hide-pdf-open-icon="hidePdfOpenIcon"
            :hide-pdf-download-icon="hideDocumentDownload"
          ></pdf-js>
        </div>
      </transition>

      <!-- loading display -->
      <info-card v-if="!isLoaded" :textRows="[$t('isLoading.moduleIsGettingLoaded')]">
        <template v-slot:title>{{ $t('isLoading.title') }}</template>
      </info-card>
      <info-card v-if="isLoaded">
        <template v-slot:title>{{ $t('fahrdienst.noDocumentSelected.title') }}</template>
        <template v-slot:content>
          <div>{{ $t('fahrdienst.noDocumentSelected') }}</div>
        </template>
      </info-card>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import SortMenu from '@/components/menus/SortMenu';
import LayoutContainer from '@/components/container/LayoutContainer';
import FullHeightContainer from '@/components/container/FullHeightContainer';
import FahrdienstDocList from '@/components/fahrdienstDocs/FahrdienstDocList';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import PdfJs from '@/components/pdf/PdfJs';
import InfoCard from '@/components/container/InfoCard';
import index from './index';
import DB from './storeNonReactive';
import fahrdienstUtil from './fahrdienstUtil';
import dateUtils from '@/common/dateUtils';
import uiHelpers from '@/common/uiHelpers';
import fahrdienstMixin from '@/modules/fahrdienst/fahrdienstMixin';

import { ROUTE_FAHRDIENST, ROUTE_FAHRDIENST_DAY, ROUTE_FAHRDIENST_DOC_ONLY, ROUTE_FAHRDIENST_DUTY_DOC } from './routes';
import { LOCAL_STORAGE_DOCUMENT_SORTER_NAME } from '@/common/constants';

export default {
  name: 'Fahrdienst',
  mixins: [common, configMixin, dateTimeMixin, fahrdienstMixin],
  components: {
    ResponsiveDrawerLayout,
    SortMenu,
    LayoutContainer,
    FullHeightContainer,
    FahrdienstDocList,
    PdfJs,
    InfoCard,
  },
  created() {
    this.init();
  },
  data() {
    return {
      relatedDocsOnly: {},
      updateRequired: false, // helper property used to indicate the FahrdienstDocsByDocType that an update is required, namely when a document has
      // been marked as read/unread. This fact changes the sort of the documents which cannot be detected by the subcomponent
      sorterName: localStorage.getItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAME) || 'byImportance',
    };
  },
  computed: {
    ...mapState('fahrdienst', ['fahrdienstDutiesById', 'isLoaded', 'filesById', 'files', 'displayMode', 'unreadCount']),
    zirkularTypeWithFiles() {
      return this.fahrdienstZirkularTypeNames
        .filter((zirkular) => {
          const files = this.groupedFiles[zirkular.id];
          return files && files.length > 0;
        })
        .sort((a, b) => {
          return a.sort - b.sort;
        });
    },
    groupedFiles() {
      const groups = {};
      for (let zirkularId of this.fahrdienstZirkularTypeIds) {
        if (this.relatedDocsOnly[zirkularId]) {
          (this.noDutyFilesMatch || []).forEach((file) => {
            if (zirkularId == file.typeIdFk) {
              if (!groups[file.typeIdFk]) {
                groups[file.typeIdFk] = [];
              }
              if (!groups[file.typeIdFk].some((el) => el.id == file.id)) {
                groups[file.typeIdFk].push(file);
              }
            }
          });
        } else {
          (this.noDutyFilesNoMatch || []).forEach((file) => {
            if (zirkularId == file.typeIdFk) {
              if (!groups[file.typeIdFk]) {
                groups[file.typeIdFk] = [];
              }
              if (!groups[file.typeIdFk].some((el) => el.id == file.id)) {
                groups[file.typeIdFk].push(file);
              }
            }
          });
        }
      }
      return groups;
    },
    moduleConfig() {
      return this.getModuleConfig(index.moduleType, this.$route.params.instance);
    },
    hideDocumentDownload() {
      return this.moduleConfig.hideDocumentDownload ? true : false;
    },
    dutyFilesHeight() {
      return 60 + 90 * Math.min(this.dutyFiles.length, 2);
    },
    duty() {
      return this.dutyId ? this.fahrdienstDutiesById[this.dutyId] : null;
    },
    dutyTitle() {
      return this.duty ? this.duty.title : '';
    },
    downloadFileName() {
      const selectedDoc = this.filesById[this.$route.params.documentId];
      if (!selectedDoc) return '';
      const { filename, title } = selectedDoc;
      const extension = filename.split('.')[1];
      if (!extension) return '';
      return `${title}.${extension}`;
    },
    displayedDocument() {
      return this.$route.params.documentId ? this.filesById[this.$route.params.documentId] : null;
    },
    displayedDocumentUrl() {
      return this.constructPdfJsFileUrl(index.api, this.displayedDocument, 'filename');
    },
    dutyFiles() {
      let { dateYYYYMMDD } = this.$route.params;
      if (!dateYYYYMMDD) return [];
      let date = dateUtils.parseYYYYMMDD(dateYYYYMMDD);

      const allDutyFiles = fahrdienstUtil.findDutiesWithFilesValidAt({
        date,
        dutyTitle: this.dutyTitle,
        dutyFilesTypeIdFk: this.fahrdienstDutyTypeIds,
        files: this.files,
        instance: this.instance,
      });

      // Check if there is more than one file and if so,
      // check if one file has a higher priority,
      // then remove the low prio file(s).

      let maxPriority = 0;

      allDutyFiles.forEach((f) => {
        if (f.priority > maxPriority) {
          maxPriority = f.priority;
        }
      });

      const filteredDutyFiles = allDutyFiles.filter((f) => f.priority == maxPriority);
      return filteredDutyFiles;
    },
    hidePdfOpenIcon() {
      return this.moduleConfig.hidePdfOpenIcon !== undefined ? this.moduleConfig.hidePdfOpenIcon : false;
    },
    noDutyFilesMatch() {
      let { instance, dutyId, dateYYYYMMDD } = this.$route.params;
      if (!dateYYYYMMDD) return [];
      let dutyTitleMustMatch = true;
      let date = dateUtils.parseYYYYMMDD(dateYYYYMMDD);
      let files = fahrdienstUtil.findDutiesWithFilesValidAt({
        dutyFilesTypeIdFk: this.fahrdienstZirkularTypeIds,
        dutyTitleMustMatch,
        date,
        dutyTitle: this.dutyTitle,
        files: this.files,
        instance: Number(instance),
      });
      return files;
    },
    noDutyFilesNoMatch() {
      let { instance, dutyId, dateYYYYMMDD } = this.$route.params;
      if (!dateYYYYMMDD) return [];
      let dutyTitleMustMatch = false;
      let date = dateUtils.parseYYYYMMDD(dateYYYYMMDD);
      let files = fahrdienstUtil.findDutiesWithFilesValidAt({
        dutyFilesTypeIdFk: this.fahrdienstZirkularTypeIds,
        dutyTitleMustMatch,
        date,
        dutyTitle: this.dutyTitle,
        files: this.files,
        instance: Number(instance),
        filterbyPriority: false,
      });
      return files;
    },
  },
  methods: {
    ...mapMutations('fahrdienst', ['setDisplayedDocument', 'displayDocument']),
    init() {
      if (!this.isLoaded) return;
      // console.debug("Fahrdienst init")
      let { instance, dutyId, dateYYYYMMDD, documentId } = this.$route.params;
      let today = new Date();
      let routeName = this.$route.name;
      // Choose today in case no duty / date has been chosen
      if (routeName === ROUTE_FAHRDIENST) {
        this.$router.push({
          name: ROUTE_FAHRDIENST_DAY,
          params: { instance, dateYYYYMMDD: dateUtils.formatDateAsYYYYMMDD(today) },
        });
      } else if (routeName === 'ROUTE_FAHRDIENST_DUTY') {
        if (this.duty) {
          this.onDocumentSelected(this.dutyFiles[0]);
        }
      } else if (routeName === 'ROUTE_FAHRDIENST_DUTY') {
        this.onDocumentSelected(this.filesById[this.duty.id]);
      } else {
        let file = this.filesById[documentId];
        if (file) {
          uiHelpers.scrollToFileOfFileList(file, 2000);
          uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, file, 2000);
        }
      }
      for (let zirkular of this.zirkularTypeWithFiles) {
        if (this.relatedDocsOnly[zirkular.id] === undefined) {
          this.relatedDocsOnly = Object.assign({}, this.relatedDocsOnly, { [zirkular.id]: true });
        }
      }
    },
    onDocumentSelected(document) {
      if (!document) return;
      // use != instead of !== because the route param is a string whereas document.id is a Number
      let isInfoFile = document && document.typeIdFk === DB.infoFileTypeByInstance[document.inst];
      // console.debug('isInfoFile', isInfoFile, document.typeIdFk, 'DB.infoFileTypeByInstance', DB.infoFileTypeByInstance);
      let { instance, dutyId, dateYYYYMMDD, documentId } = this.$route.params;
      if (documentId != document.id) {
        if (dutyId) {
          this.$router.push({
            name: ROUTE_FAHRDIENST_DUTY_DOC,
            params: { instance: document.inst, dutyId, dateYYYYMMDD, documentId: document.id },
          });
        } else {
          // console.debug('might be ROUTE_FAHRDIENST_DOC_ONLY', this.$route.name);
          this.$router.push({
            name: ROUTE_FAHRDIENST_DOC_ONLY,
            params: { instance: document.inst, dateYYYYMMDD, documentId: document.id },
          });
        }

        if (this.mdAndDown) {
          this.mx_isLeftDrawerOpen = false;
        }
      }
    },
    toggleRelated(zirkularId) {
      this.$set(this.relatedDocsOnly, zirkularId, !this.relatedDocsOnly[zirkularId]);
    },
  },
  watch: {
    unreadCount() {
      // console.debug('unreadCount changed');
      this.updateRequired = !this.updateRequired;
    },
    $route(newValue, oldValue) {
      if (newValue !== oldValue) this.init();
    },
    isLoaded() {
      this.init();
    },
    duty(newDuty, oldDuty) {
      // console.debug('newDuty', newDuty, 'oldDuty', oldDuty);
    },
    sorterName(newValue) {
      localStorage.setItem(LOCAL_STORAGE_DOCUMENT_SORTER_NAME, newValue);
    },
  },
};
</script>

<style scoped>
.fill-width-absolute {
  position: absolute;
  left: 0;
  right: 0;
  padding: 0;
}
.zirkular-header {
  margin: 0;
}
.zirkular-header-light {
  background: var(--v-surface-base, #eae5e5);
}
.zirkular-header-dark {
  background: var(--v-surface-base, #706e6e);
}
.zirkular-content >>> .v-list {
  padding-bottom: 0 !important;
}
</style>
